import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendarStyle.css';
import CalendarToolbar from './CalendarToolbar';
import CalendarEvent from './CalendarEvent';
import moment from 'moment-timezone';
import { withNamespaces } from 'react-i18next';
import ReduxBlockUi from 'react-block-ui/redux';
import 'react-block-ui/style.css';
import CalendarWeekView from './CalendarWeekView';
import CalendarDayView from './CalendarDayView';
import CalendarNext7DaysWeekView from './CalendarNext7DaysWeekView';
import CalendarNext7DaysDayView from './CalendarNext7DaysDayView';
import CalendarEventWrapper from './CalendarEventWrapper';
import CalendarEventContainerWrapper from './CalendarEventContainerWrapper';
import utils from '../../../common/services/utils';
import availabilityUtils from '../availabilityUtils';

const localizer = momentLocalizer(moment);

export const CALENDAR_VIEW_WEEK = 'week';
export const CALENDAR_VIEW_DAY = 'day';
export const CALENDAR_VIEW_NEXT_7_DAYS_WEEK = 'next7days_week';
export const CALENDAR_VIEW_NEXT_7_DAYS_DAY = 'next7days_day';

export const getDefaultCalendarView = (canNavigate) => window.innerWidth > 767 ?
	(canNavigate ? CALENDAR_VIEW_WEEK : CALENDAR_VIEW_NEXT_7_DAYS_WEEK) :
	(canNavigate ? CALENDAR_VIEW_DAY : CALENDAR_VIEW_NEXT_7_DAYS_DAY);

export const dateRangeByView = (view, date, startOfWeek) => {
	let from, to;

	switch (view) {
		case CALENDAR_VIEW_WEEK:
			let firstDayOfWeek = moment(date).startOf('isoWeek');
			if (startOfWeek) {
				const currentWeekday = moment(date).isoWeekday();
				startOfWeek = currentWeekday < startOfWeek ? startOfWeek - 7 : startOfWeek;
				firstDayOfWeek = moment(date).isoWeekday(startOfWeek);
			}
			from = firstDayOfWeek.startOf('day').toDate();
			to = firstDayOfWeek.add(6, 'day').endOf('day').toDate();
			break;
		case CALENDAR_VIEW_DAY:
			from = moment(date).add(-8, 'days').startOf('day').toDate();
			to = moment(date).add(8, 'days').endOf('day').toDate();
			break;
		case CALENDAR_VIEW_NEXT_7_DAYS_WEEK:
		case CALENDAR_VIEW_NEXT_7_DAYS_DAY:		// load lessons for the next days to be able to skip days without lessons when navigating through the calendar
			from = moment(date).startOf('day').toDate();
			to = moment(date).add(6, 'days').endOf('day').toDate();
			break;
		//case CALENDAR_VIEW_NEXT_7_DAYS_DAY:
		//from = moment(date).startOf('day').toDate();
		//to = moment(date).endOf('day').toDate();
		//break;
	}

	return { from, to };
};

export const selectableDateRangeByView = (view, initialDate) => {
	let from = null;
	let to = null;

	switch (view) {
		case CALENDAR_VIEW_WEEK:
		case CALENDAR_VIEW_DAY:
			break;
		case CALENDAR_VIEW_NEXT_7_DAYS_WEEK:
			from = to = initialDate;
			break;
		case CALENDAR_VIEW_NEXT_7_DAYS_DAY:
			from = initialDate;
			to = moment(initialDate).add(6, 'days').toDate();
			break;
	}

	return { from, to };
};

class Calendar extends Component {
	constructor(props) {
		super(props);
		this.handleCalendarNavigate = this.handleCalendarNavigate.bind(this);
		this.handleCalendarEventSelect = this.handleCalendarEventSelect.bind(this);

		this.state = {
			selectedLesson: null
		};
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.lessons && nextProps.center && nextProps.date && nextProps.initialDate && moment(nextProps.date).format('YYYYMMDD') === moment(nextProps.initialDate).format('YYYYMMDD')) {
			// move to the first date with classes when opening in a mobile view and there are no classes on the initial date
			const nextView = getDefaultCalendarView(nextProps.canNavigate);
			if (nextView === CALENDAR_VIEW_DAY || nextView === CALENDAR_VIEW_NEXT_7_DAYS_DAY) {
				const enableUserTimeZone = nextProps.enableUserTimeZone;
				const center = nextProps.center;
				const currentDate = moment(nextProps.date);
				const sortedLessons = nextProps.lessons.sort((a, b) => moment(a.start) - moment(b.start));
				const currentDateLesson = sortedLessons.find(x => (enableUserTimeZone ? utils.convertToUserTime(x.start, center) : moment(x.start)).format('YYYYMMDD') === currentDate.format('YYYYMMDD'));
				const futureDateLesson = sortedLessons.find(x => (enableUserTimeZone ? utils.convertToUserTime(x.start, center) : moment(x.start)).startOf('day').isAfter(currentDate));
				if (!currentDateLesson && futureDateLesson) {
					this.handleCalendarNavigate(new Date(moment(futureDateLesson.start)));
				}
			}
		}
	}

	handleCalendarNavigate(date) {
		this.props.onNavigate(date);
	}

	handleCalendarEventSelect(event) {
		this.props.onSelectLesson(event.lesson, this.props.lessonType);
		this.setState({ selectedLesson: event.lesson });
	}

	getLessonCss(event, lessonType) {
		const lesson = event.lesson;
		const { selectedLesson } = this.state;
		const isSelected = selectedLesson && selectedLesson.programId === lesson.programId && selectedLesson.start === lesson.start;

		let className = 'green';

		const availability = availabilityUtils.getLessonAvailability(lesson, lessonType);

		switch (availability) {
			case 'Yes':
				className = 'green';
				break;
			case 'No':
				className = 'red';
				break;
			case 'Limited':
			case 'LastSpot':
				className = 'orange';
				break;
		}

		return { className: `${className} ${isSelected ? 'selected' : ''}` };
	}

	render() {

		const { lessonType, lessons, enableUserTimeZone, calendarBlock, calendarUnblock, date, canNavigate, initialDate, startOfWeek, center, t } = this.props;

		lessons.forEach(lesson => {
			lesson.startMoment = enableUserTimeZone && center ? utils.convertToUserTime(lesson.start, center) : moment(lesson.start);
			lesson.endMoment = enableUserTimeZone && center ? utils.convertToUserTime(lesson.end, center) : moment(lesson.end);
		});

		const sortedLessons = lessons.sort(function (a, b) {
			return a.startMoment.format('HH') - b.startMoment.format('HH');
		});

		const hasMultiDayEvent = sortedLessons.length > 0 ? sortedLessons.some(x => moment(x.startMoment).startOf('day').isBefore(moment(x.endMoment).startOf('day'))) : false;

		const hourStart = sortedLessons.length > 0 ? sortedLessons[0].startMoment.hour() : 9;
		const hourEnd = sortedLessons.length > 0 ? (hasMultiDayEvent ? 23 : Math.max(20, ...sortedLessons.map(x => x.endMoment.hour()))) : 20;

		const calendarView = getDefaultCalendarView(canNavigate);
		const selectableDateRange = selectableDateRangeByView(calendarView, initialDate);

		const bigCalendarEvents = lessons
			.filter(x => !selectableDateRange.from || selectableDateRange.from === selectableDateRange.to ||
				// next7days_day view loads more data than allowed to book (next 6 days on each forward navigation)
				(moment(x.startMoment).startOf('day') >= moment(selectableDateRange.from).startOf('day') && moment(x.startMoment).startOf('day') <= moment(selectableDateRange.to).startOf('day')))
			.map(lesson => {
				return {
					allDay: false,
					start: lesson.startMoment.toDate(),
					end: lesson.endMoment.toDate(),
					title: lesson.grade.name,
					availability: availabilityUtils.getLessonAvailability(lesson, lessonType),
					lesson
				};
			});

		const formats = {
			dayFormat: (date, culture, localizer) => localizer.format(date, 'ddd DD/MM', culture),
			timeGutterFormat: (date, culture, localizer) => localizer.format(date, 'LT', culture),
			eventTimeRangeFormat: ({ start, end }, culture, localizer) => `${localizer.format(start, 'LT', culture)} - ${localizer.format(end, 'LT', culture)}`
		};

		return (
			<div>
				<ReduxBlockUi block={calendarBlock} unblock={calendarUnblock}>
					<div className='availability-legend hidden-xs'>
						<span className='green' />{t('FO_Booking_Availability_Yes')}
						<span className='orange' />{t('FO_Booking_Availability_Limited')}
						<span className='red' />{t('FO_Booking_Availability_No')}
					</div>
					<div className='calendar-wrapper'>
						<BigCalendar
							date={date}
							view={calendarView}
							min={moment().startOf('day').hour(hourStart).toDate()}
							max={moment().endOf('day').hour(hourEnd).toDate()}
							events={bigCalendarEvents}
							onNavigate={this.handleCalendarNavigate}
							onSelectEvent={this.handleCalendarEventSelect}
							components={{
								toolbar: CalendarToolbar({ initialDate }),
								event: CalendarEvent,
								eventWrapper: CalendarEventWrapper({ hourStart }),
								eventContainerWrapper: CalendarEventContainerWrapper({ hourStart, hourEnd })
							}}
							messages={{ allDay: '' }}
							eventPropGetter={(event) => this.getLessonCss(event, lessonType)}
							views={{ day: CalendarDayView, week: CalendarWeekView, next7days_day: CalendarNext7DaysDayView, next7days_week: CalendarNext7DaysWeekView }}
							localizer={localizer}
							formats={formats}
							startOfWeek={startOfWeek}	/* custom props are passed to the view */
							showMultiDayTimes
						/>
					</div>
				</ReduxBlockUi>
			</div >
		);
	}
}

Calendar.propTypes = {
	date: PropTypes.object,
	lessonType: PropTypes.oneOf(['', 'normal', 'trial', 'makeup']),
	lessons: PropTypes.arrayOf(PropTypes.shape({
		programId: PropTypes.number,
		grade: PropTypes.shape({
			name: PropTypes.string
		}),
		start: PropTypes.string,
		end: PropTypes.string
	})),
	enableUserTimeZone: PropTypes.bool,
	onNavigate: PropTypes.func.isRequired,
	onSelectLesson: PropTypes.func.isRequired
};

export default withNamespaces()(Calendar);